import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  QrCode, CheckCircle, XCircle, AlertCircle, Wifi, WifiOff,
  Camera, RefreshCw, Download, Upload, Users, Ticket,
  ChevronLeft, Settings, Loader2, Volume2, VolumeX
} from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ticketsApi, eventsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

// IndexedDB helper for offline storage
const DB_NAME = 'BravoTicketsDB';
const DB_VERSION = 1;
const TICKETS_STORE = 'tickets';
const VALIDATIONS_STORE = 'validations';

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Store for cached tickets
      if (!db.objectStoreNames.contains(TICKETS_STORE)) {
        const ticketsStore = db.createObjectStore(TICKETS_STORE, { keyPath: 'ticket_code' });
        ticketsStore.createIndex('event_id', 'event_id', { unique: false });
        ticketsStore.createIndex('holder_dni', 'holder_dni', { unique: false });
      }
      
      // Store for offline validations (to sync later)
      if (!db.objectStoreNames.contains(VALIDATIONS_STORE)) {
        const validationsStore = db.createObjectStore(VALIDATIONS_STORE, { keyPath: 'id', autoIncrement: true });
        validationsStore.createIndex('synced', 'synced', { unique: false });
      }
    };
  });
};

const TicketScannerPage = () => {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [cachedTickets, setCachedTickets] = useState([]);
  const [pendingValidations, setPendingValidations] = useState([]);
  const [stats, setStats] = useState({ validated: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  
  // Audio refs for feedback
  const successAudioRef = useRef(null);
  const errorAudioRef = useRef(null);

  // Check online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load events on mount
  useEffect(() => {
    loadEvents();
  }, []);

  // Load cached data when event is selected
  useEffect(() => {
    if (selectedEvent) {
      loadCachedTickets(selectedEvent.id);
      loadPendingValidations();
    }
  }, [selectedEvent]);

  // Sync pending validations when coming online
  useEffect(() => {
    if (isOnline && pendingValidations.length > 0) {
      syncPendingValidations();
    }
  }, [isOnline]);

  const loadEvents = async () => {
    try {
      const response = await eventsApi.getAll();
      // Filter to only upcoming events
      const upcomingEvents = response.filter(e => new Date(e.date) >= new Date());
      setEvents(upcomingEvents);
      
      // Auto-select if only one event
      if (upcomingEvents.length === 1) {
        setSelectedEvent(upcomingEvents[0]);
      }
    } catch (error) {
      console.error('Error loading events:', error);
      toast.error('Error al cargar eventos');
    } finally {
      setLoading(false);
    }
  };

  const loadCachedTickets = async (eventId) => {
    try {
      const db = await openDB();
      const transaction = db.transaction(TICKETS_STORE, 'readonly');
      const store = transaction.objectStore(TICKETS_STORE);
      const index = store.index('event_id');
      const request = index.getAll(eventId);
      
      request.onsuccess = () => {
        const tickets = request.result || [];
        setCachedTickets(tickets);
        const validated = tickets.filter(t => t.validated).length;
        setStats({ validated, total: tickets.length });
      };
    } catch (error) {
      console.error('Error loading cached tickets:', error);
    }
  };

  const loadPendingValidations = async () => {
    try {
      const db = await openDB();
      const transaction = db.transaction(VALIDATIONS_STORE, 'readonly');
      const store = transaction.objectStore(VALIDATIONS_STORE);
      const index = store.index('synced');
      const request = index.getAll(false);
      
      request.onsuccess = () => {
        setPendingValidations(request.result || []);
      };
    } catch (error) {
      console.error('Error loading pending validations:', error);
    }
  };

  // Download tickets for offline use
  const downloadTicketsForOffline = async () => {
    if (!selectedEvent) return;
    
    try {
      setLoading(true);
      const response = await ticketsApi.getByEvent(selectedEvent.id);
      const tickets = response || [];
      
      const db = await openDB();
      const transaction = db.transaction(TICKETS_STORE, 'readwrite');
      const store = transaction.objectStore(TICKETS_STORE);
      
      // Clear old tickets for this event
      const index = store.index('event_id');
      const deleteRequest = index.openCursor(selectedEvent.id);
      
      deleteRequest.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };
      
      // Add new tickets
      tickets.forEach(ticket => {
        store.put({
          ...ticket,
          event_id: selectedEvent.id,
          validated: ticket.status === 'validated',
          cached_at: new Date().toISOString()
        });
      });
      
      transaction.oncomplete = () => {
        loadCachedTickets(selectedEvent.id);
        toast.success(`${tickets.length} tickets descargados para uso offline`);
      };
    } catch (error) {
      console.error('Error downloading tickets:', error);
      toast.error('Error al descargar tickets');
    } finally {
      setLoading(false);
    }
  };

  // Sync pending validations to server
  const syncPendingValidations = async () => {
    if (pendingValidations.length === 0) return;
    
    try {
      const db = await openDB();
      
      for (const validation of pendingValidations) {
        try {
          await ticketsApi.validate(validation.ticket_code);
          
          // Mark as synced
          const transaction = db.transaction(VALIDATIONS_STORE, 'readwrite');
          const store = transaction.objectStore(VALIDATIONS_STORE);
          validation.synced = true;
          store.put(validation);
        } catch (error) {
          console.error('Error syncing validation:', error);
        }
      }
      
      loadPendingValidations();
      toast.success('Validaciones sincronizadas');
    } catch (error) {
      console.error('Error syncing:', error);
    }
  };

  // Play sound feedback
  const playSound = (success) => {
    if (!soundEnabled) return;
    
    try {
      // Using Web Audio API for simple sounds
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      if (success) {
        oscillator.frequency.value = 800;
        gainNode.gain.value = 0.3;
        oscillator.start();
        setTimeout(() => {
          oscillator.frequency.value = 1200;
        }, 100);
        setTimeout(() => oscillator.stop(), 200);
      } else {
        oscillator.frequency.value = 300;
        gainNode.gain.value = 0.3;
        oscillator.start();
        setTimeout(() => oscillator.stop(), 300);
      }
    } catch (error) {
      console.error('Audio error:', error);
    }
  };

  // Validate ticket (works offline)
  const validateTicket = useCallback(async (qrData) => {
    // Parse QR data: BRAVO|DNI|TICKET_CODE|EVENT_ID
    const parts = qrData.split('|');
    if (parts.length < 3 || parts[0] !== 'BRAVO') {
      setLastResult({ success: false, message: 'QR inválido', type: 'invalid' });
      playSound(false);
      return;
    }
    
    const [, dni, ticketCode, eventId] = parts;
    
    // Check if ticket exists in cache
    const cachedTicket = cachedTickets.find(t => 
      t.ticket_code === ticketCode || t.holder_dni === dni
    );
    
    if (!cachedTicket) {
      // Try online validation if available
      if (isOnline) {
        try {
          const response = await ticketsApi.validate(ticketCode);
          setLastResult({ 
            success: true, 
            message: '¡Entrada válida!',
            ticket: response,
            type: 'success'
          });
          playSound(true);
          
          // Update local cache
          await loadCachedTickets(selectedEvent.id);
          return;
        } catch (error) {
          setLastResult({ 
            success: false, 
            message: error.response?.data?.detail || 'Ticket no encontrado',
            type: 'error'
          });
          playSound(false);
          return;
        }
      } else {
        setLastResult({ 
          success: false, 
          message: 'Ticket no encontrado en caché offline',
          type: 'not_found'
        });
        playSound(false);
        return;
      }
    }
    
    // Check if already validated
    if (cachedTicket.validated) {
      setLastResult({ 
        success: false, 
        message: 'Ticket ya fue validado',
        ticket: cachedTicket,
        type: 'already_validated'
      });
      playSound(false);
      return;
    }
    
    // Validate locally
    try {
      const db = await openDB();
      
      // Update ticket as validated in cache
      const ticketTransaction = db.transaction(TICKETS_STORE, 'readwrite');
      const ticketStore = ticketTransaction.objectStore(TICKETS_STORE);
      cachedTicket.validated = true;
      cachedTicket.validated_at = new Date().toISOString();
      cachedTicket.validated_by = user?.username || 'staff';
      ticketStore.put(cachedTicket);
      
      // If online, validate on server too
      if (isOnline) {
        try {
          await ticketsApi.validate(ticketCode);
        } catch (error) {
          // Store for later sync
          const validationTransaction = db.transaction(VALIDATIONS_STORE, 'readwrite');
          const validationStore = validationTransaction.objectStore(VALIDATIONS_STORE);
          validationStore.add({
            ticket_code: ticketCode,
            event_id: selectedEvent.id,
            validated_at: new Date().toISOString(),
            validated_by: user?.username || 'staff',
            synced: false
          });
        }
      } else {
        // Store for later sync
        const validationTransaction = db.transaction(VALIDATIONS_STORE, 'readwrite');
        const validationStore = validationTransaction.objectStore(VALIDATIONS_STORE);
        validationStore.add({
          ticket_code: ticketCode,
          event_id: selectedEvent.id,
          validated_at: new Date().toISOString(),
          validated_by: user?.username || 'staff',
          synced: false
        });
      }
      
      setLastResult({ 
        success: true, 
        message: '¡Entrada válida!',
        ticket: cachedTicket,
        type: 'success'
      });
      playSound(true);
      
      // Update stats
      setStats(prev => ({ ...prev, validated: prev.validated + 1 }));
      loadPendingValidations();
      
    } catch (error) {
      console.error('Validation error:', error);
      setLastResult({ 
        success: false, 
        message: 'Error al validar',
        type: 'error'
      });
      playSound(false);
    }
  }, [cachedTickets, isOnline, selectedEvent, user, soundEnabled]);

  // Start QR scanner
  const startScanner = async () => {
    if (!scannerRef.current) return;
    
    try {
      html5QrCodeRef.current = new Html5Qrcode('qr-reader');
      
      await html5QrCodeRef.current.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        (decodedText) => {
          // Vibrate on scan
          if (navigator.vibrate) {
            navigator.vibrate(100);
          }
          validateTicket(decodedText);
        },
        (errorMessage) => {
          // Ignore scan errors
        }
      );
      
      setScanning(true);
    } catch (error) {
      console.error('Scanner error:', error);
      toast.error('Error al iniciar cámara. Verifica los permisos.');
    }
  };

  // Stop scanner
  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
      } catch (error) {
        console.error('Error stopping scanner:', error);
      }
    }
    setScanning(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  // Check permissions
  if (!hasPermission('tickets.validate')) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center">
          <XCircle className="mx-auto text-red-500 mb-4" size={48} />
          <h2 className="text-xl font-bold text-white mb-2">Acceso Denegado</h2>
          <p className="text-[#71717A]">No tienes permiso para validar tickets.</p>
          <Button onClick={() => navigate('/admin')} className="mt-4">
            Volver al Panel
          </Button>
        </div>
      </div>
    );
  }

  // Event selection screen
  if (!selectedEvent) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button 
            onClick={() => navigate('/admin')}
            className="flex items-center text-[#71717A] hover:text-white"
          >
            <ChevronLeft size={24} />
          </button>
          <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
            isOnline ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            {isOnline ? <Wifi size={16} /> : <WifiOff size={16} />}
            <span className="text-xs font-medium">{isOnline ? 'Online' : 'Offline'}</span>
          </div>
        </div>

        <div className="text-center mb-8">
          <QrCode className="mx-auto text-[#D4AF37] mb-4" size={48} />
          <h1 className="text-2xl font-black uppercase tracking-tight text-white">
            Scanner de Tickets
          </h1>
          <p className="text-[#71717A] mt-2">Selecciona el evento</p>
        </div>

        {loading ? (
          <div className="flex justify-center">
            <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-[#71717A]">No hay eventos próximos</p>
          </div>
        ) : (
          <div className="space-y-3">
            {events.map(event => (
              <motion.button
                key={event.id}
                onClick={() => setSelectedEvent(event)}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-[#121212] border border-[#27272A] p-4 text-left hover:border-[#D4AF37]/50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  {event.image_url && (
                    <img 
                      src={event.image_url} 
                      alt={event.title}
                      className="w-16 h-16 object-cover"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-white truncate">{event.title}</h3>
                    <p className="text-sm text-[#71717A]">
                      {new Date(event.date).toLocaleDateString('es-AR', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        timeZone: 'America/Argentina/Buenos_Aires'
                      })}
                    </p>
                  </div>
                  <ChevronLeft className="text-[#71717A] rotate-180" size={20} />
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col">
      {/* Header */}
      <div className="bg-[#121212] border-b border-[#27272A] px-4 py-3 safe-area-top">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => {
              stopScanner();
              setSelectedEvent(null);
            }}
            className="flex items-center text-[#71717A] hover:text-white"
          >
            <ChevronLeft size={24} />
          </button>
          
          <div className="text-center flex-1 mx-4">
            <h1 className="font-bold text-white truncate text-sm">{selectedEvent.title}</h1>
            <div className="flex items-center justify-center gap-2 mt-1">
              <span className="text-xs text-[#D4AF37]">{stats.validated}/{stats.total}</span>
              <div className={`flex items-center gap-1 text-xs ${
                isOnline ? 'text-green-400' : 'text-red-400'
              }`}>
                {isOnline ? <Wifi size={12} /> : <WifiOff size={12} />}
              </div>
            </div>
          </div>
          
          <button 
            onClick={() => setShowSettings(!showSettings)}
            className="text-[#71717A] hover:text-white p-2"
          >
            <Settings size={20} />
          </button>
        </div>
      </div>

      {/* Settings Panel */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-[#1A1A1A] border-b border-[#27272A] overflow-hidden"
          >
            <div className="p-4 space-y-4">
              {/* Sound toggle */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-white">Sonido</span>
                <button
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className={`p-2 rounded ${soundEnabled ? 'bg-[#D4AF37] text-black' : 'bg-[#27272A] text-[#71717A]'}`}
                >
                  {soundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
                </button>
              </div>
              
              {/* Download for offline */}
              <Button
                onClick={downloadTicketsForOffline}
                disabled={!isOnline || loading}
                className="w-full bg-[#27272A] hover:bg-[#3A3A3A] text-white"
              >
                <Download size={18} className="mr-2" />
                Descargar tickets ({cachedTickets.length} en caché)
              </Button>
              
              {/* Sync pending */}
              {pendingValidations.length > 0 && (
                <Button
                  onClick={syncPendingValidations}
                  disabled={!isOnline}
                  className="w-full bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30"
                >
                  <Upload size={18} className="mr-2" />
                  Sincronizar {pendingValidations.length} validaciones
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Scanner Area */}
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {!scanning ? (
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-center"
          >
            <div className="w-64 h-64 border-2 border-dashed border-[#27272A] rounded-2xl flex items-center justify-center mb-6">
              <Camera className="text-[#71717A]" size={64} />
            </div>
            <Button
              onClick={startScanner}
              className="bg-[#D4AF37] text-black hover:bg-[#D4AF37]/90 font-bold px-8 py-6 text-lg"
            >
              <QrCode className="mr-2" size={24} />
              Iniciar Scanner
            </Button>
            <p className="text-[#71717A] text-sm mt-4">
              {cachedTickets.length > 0 
                ? `${cachedTickets.length} tickets en caché para uso offline`
                : 'Descarga tickets para usar offline'
              }
            </p>
          </motion.div>
        ) : (
          <div className="w-full max-w-sm">
            {/* QR Scanner */}
            <div 
              id="qr-reader" 
              ref={scannerRef}
              className="w-full aspect-square bg-black rounded-xl overflow-hidden"
            />
            
            {/* Stop button */}
            <Button
              onClick={stopScanner}
              variant="outline"
              className="w-full mt-4 border-[#27272A] text-white"
            >
              Detener Scanner
            </Button>
          </div>
        )}
      </div>

      {/* Result Panel */}
      <AnimatePresence>
        {lastResult && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className={`p-6 ${
              lastResult.success 
                ? 'bg-green-500' 
                : lastResult.type === 'already_validated'
                  ? 'bg-yellow-500'
                  : 'bg-red-500'
            }`}
          >
            <div className="flex items-start gap-4">
              {lastResult.success ? (
                <CheckCircle className="text-white flex-shrink-0" size={32} />
              ) : lastResult.type === 'already_validated' ? (
                <AlertCircle className="text-white flex-shrink-0" size={32} />
              ) : (
                <XCircle className="text-white flex-shrink-0" size={32} />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-lg">{lastResult.message}</p>
                {lastResult.ticket && (
                  <div className="mt-2 text-white/80 text-sm">
                    <p>DNI: <span className="font-mono">{lastResult.ticket.holder_dni}</span></p>
                    <p>Código: <span className="font-mono">{lastResult.ticket.ticket_code}</span></p>
                    {lastResult.ticket.tier_name && (
                      <p>Tipo: {lastResult.ticket.tier_name}</p>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={() => setLastResult(null)}
                className="text-white/60 hover:text-white"
              >
                ✕
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats Bar */}
      <div className="bg-[#121212] border-t border-[#27272A] px-4 py-3 safe-area-bottom">
        <div className="flex items-center justify-around text-center">
          <div>
            <p className="text-2xl font-black text-green-400">{stats.validated}</p>
            <p className="text-xs text-[#71717A] uppercase">Validados</p>
          </div>
          <div className="w-px h-8 bg-[#27272A]" />
          <div>
            <p className="text-2xl font-black text-white">{stats.total - stats.validated}</p>
            <p className="text-xs text-[#71717A] uppercase">Pendientes</p>
          </div>
          <div className="w-px h-8 bg-[#27272A]" />
          <div>
            <p className="text-2xl font-black text-[#D4AF37]">{stats.total}</p>
            <p className="text-xs text-[#71717A] uppercase">Total</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketScannerPage;
